import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocalStorageService } from '../storage/local-storage.service';
import { TimezoneService } from '../date/timezone.service';
import { QuoteDetailType, RecentQuotes, RecentQuotesItem, SecuritySymbol } from '../../domain/public-api';
import { RecentQuotesRestService } from './recent-quotes-rest.service';
import { RecentSymbolsQueueService } from './recent-symbols-queue.service';

/**
 * Recent Quotes Service
 */
@Injectable({
  providedIn: 'root'
})
export class RecentQuotesService {
  constructor(private recentQuotesRestService: RecentQuotesRestService,
              private recentSymbolsQueueService: RecentSymbolsQueueService,
              private localStorageService: LocalStorageService,
              private timezoneService: TimezoneService) {}

  retrieveRecentQuotes(quoteType: Array<QuoteDetailType>, showRecentQuotes: boolean = true,
                       hasFundamentals: boolean): Observable<RecentQuotes> {
    if (showRecentQuotes === false && this.recentSymbolsQueueService.isEmpty()) {
      return of(this.filterRecentQuotes(showRecentQuotes));
    }
    return this.recentQuotesRestService.getRecentQuotes(quoteType, hasFundamentals)
      .pipe(map((recentQuotes: RecentQuotes): RecentQuotes => this.filterRecentQuotes(showRecentQuotes, recentQuotes)));
  }

  addToRecentQuotes(symbols: Array<SecuritySymbol>, localRecentSymbolsQueue: RecentSymbolsQueueService,
                    showRecentQuotes: boolean = true): Observable<void> {
    return this.recentQuotesRestService.addToRecentQuotes(symbols)
      .pipe(map((): void => {
        if (showRecentQuotes === false) {
          localRecentSymbolsQueue.add(symbols);
        }
      }));
  }

  deleteFromRecentQuotes(recentQuotesEntry: RecentQuotesItem, showRecentQuotes: boolean = true): Observable<void> {
    const id = recentQuotesEntry.symbol === undefined ? undefined : recentQuotesEntry.symbol.id;
    return this.recentQuotesRestService.deleteRecentQuotesByIds(id)
      .pipe(map((): void => {
        if (showRecentQuotes === false) {
          this.recentSymbolsQueueService.removeByRecentQuotes(recentQuotesEntry);
        }
      }));
  }

  getShowRecentQuotesIndicator(): boolean {
    return !(this.localStorageService.getItem('showRecentQuotes') === 'false');
  }

  saveShowRecentQuotesIndicator(value: unknown): void {
    this.localStorageService.setItem('showRecentQuotes', value);
    this.recentSymbolsQueueService.clear();
  }

  saveGroupAssetsIndicator(value: boolean): boolean {
    this.localStorageService.setItem('groupAssets', value);
    return this.getGroupAssetsIndicator();
  }

  getGroupAssetsIndicator(): boolean {
    return !(this.localStorageService.getItem('groupAssets') === 'false');
  }

  private filterRecentQuotes(showRecentQuotes: boolean,
                             recentQuotesOrdered?: RecentQuotes): RecentQuotes {
    if (showRecentQuotes === false) {
      if (recentQuotesOrdered === undefined) {
        recentQuotesOrdered = {
          timestamp: this.timezoneService.getCurrentDateInServerTimezone()
        } as RecentQuotes;
      }
      recentQuotesOrdered.assetGroups = this.recentSymbolsQueueService.filterRecentQuotes(recentQuotesOrdered.assetGroups);
    }
    return recentQuotesOrdered;
  }
}

